import { request, sendWithXhr, type UploadProgress } from '@/api/client'
import { PackageParseError } from '@/api/packageParseError'
import {
  mapVersionDetail,
  normalizePlatform,
  normalizePlatforms,
  normalizeRuntimes,
  type VersionDetailDto,
} from '@/api/versionMapper'
import type {
  AppVersion,
  MainPackageInfo,
  Platform,
  VersionStatus,
} from '@/types'

/** 主包上传可选上下文（迭代 / 同草稿覆盖） */
export interface UploadMainOptions {
  app_id?: string
  version_id?: string
  signal?: AbortSignal
  onUploadProgress?: (progress: UploadProgress) => void
  /** 上传成功、即将请求 parse-result 时回调（供 UI 切到 parsing） */
  onParsingStart?: () => void
}

export interface UploadMainResult {
  upload_id: string
  app_id: string
  version_id: string
  status: VersionStatus
  mainParse: MainPackageInfo
  draft: AppVersion
  /** 来自 parse-result 的展示名；没有则前端用文件名推断 */
  appName?: string
}

export interface UploadPlatformResult {
  upload_id: string
  platform: Platform
  file_name: string
  file_size: number
  package_url?: string
  app_id: string
  version_id: string
}

/** 后端可能返回 snake_case 或 camelCase */
type RawRecord = Record<string, unknown>

function pick<T>(
  obj: RawRecord | null | undefined,
  camel: string,
  snake: string,
): T | undefined {
  if (!obj) return undefined
  const v = obj[camel] ?? obj[snake]
  return v as T | undefined
}

function asRecord(value: unknown): RawRecord | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as RawRecord
  }
  return undefined
}

interface NormalizedMainUpload {
  upload_id: string
  package_type?: string
  file_name: string
  file_size: number
  parse_status?: string
  app_id: string
  version_id: string
  status: VersionStatus
  platforms?: Platform[]
  needs_platform_packages?: boolean
  draft?: VersionDetailDto
}

function normalizeMainPackage(
  raw: RawRecord | undefined,
): VersionDetailDto['main_package'] {
  if (!raw) return undefined
  return {
    upload_id: pick<string>(raw, 'uploadId', 'upload_id'),
    file_name: pick<string>(raw, 'fileName', 'file_name'),
    file_size: pick<number>(raw, 'fileSize', 'file_size'),
    main_package_url: pick<string>(raw, 'mainPackageUrl', 'main_package_url'),
    icon_url: pick<string>(raw, 'iconUrl', 'icon_url'),
    uploaded_at: pick<string>(raw, 'uploadedAt', 'uploaded_at'),
    needs_platform_packages: pick<boolean>(
      raw,
      'needsPlatformPackages',
      'needs_platform_packages',
    ),
    runtimes: normalizeRuntimes(pick(raw, 'runtimes', 'runtimes')),
  }
}

function normalizePlatformPackages(
  raw: unknown,
): VersionDetailDto['platform_packages'] {
  if (!Array.isArray(raw)) return undefined
  return raw.flatMap((item) => {
    const p = asRecord(item) ?? {}
    const platform = normalizePlatform(pick(p, 'platform', 'platform'))
    if (!platform) return []
    return {
      platform,
      upload_id: pick<string>(p, 'uploadId', 'upload_id'),
      file_id: pick<string>(p, 'fileId', 'file_id'),
      package_url: pick<string>(p, 'packageUrl', 'package_url'),
      package_version: pick<string>(p, 'packageVersion', 'package_version'),
      file_name: pick<string>(p, 'fileName', 'file_name'),
      file_size: pick<number>(p, 'fileSize', 'file_size'),
      is_historical: pick<boolean>(p, 'isHistorical', 'is_historical'),
      from_version_id: pick<string>(p, 'fromVersionId', 'from_version_id'),
      parse_status: pick<string>(p, 'parseStatus', 'parse_status'),
    }
  })
}

/** 把 upload 响应当中的 draft（snake/camel）收成 VersionDetailDto */
export function normalizeUploadDraft(
  raw: unknown,
  fallback: {
    app_id?: string
    version_id?: string
    status?: VersionStatus
  } = {},
): VersionDetailDto {
  const d = asRecord(raw) ?? {}
  const space = asRecord(pick(d, 'space', 'space'))
  const support = asRecord(pick(d, 'support_account', 'support_account'))

  return {
    app_id: pick<string>(d, 'appId', 'app_id') ?? fallback.app_id,
    version_id:
      pick<string>(d, 'versionId', 'version_id') ?? fallback.version_id,
    version_code: pick<string>(d, 'versionCode', 'version_code'),
    status:
      pick<VersionStatus>(d, 'status', 'status') ?? fallback.status,
    platforms: normalizePlatforms(pick(d, 'platforms', 'platforms')),
    released_at: pick<string | null>(d, 'releasedAt', 'released_at') ?? null,
    last_operated_at: pick<string>(d, 'lastOperatedAt', 'last_operated_at'),
    scheduled_at: pick<string | null>(d, 'scheduledAt', 'scheduled_at') ?? null,
    is_first_version: pick<boolean>(d, 'isFirstVersion', 'is_first_version'),
    review_comment: pick<string | null>(d, 'reviewComment', 'review_comment'),
    distribution_type: pick(d, 'distributionType', 'distribution_type'),
    distribution_locked: pick<boolean>(
      d,
      'distributionLocked',
      'distribution_locked',
    ),
    listing_space_types: pick(d, 'listingSpaceTypes', 'listing_space_types'),
    space: space
      ? {
          id: pick<string>(space, 'id', 'id'),
          name: pick<string>(space, 'name', 'name'),
          icon_url: pick<string>(space, 'iconUrl', 'icon_url'),
        }
      : undefined,
    available_members: pick(d, 'availableMembers', 'available_members'),
    supported_languages: pick(d, 'supportedLanguages', 'supported_languages'),
    default_language: pick(d, 'defaultLanguage', 'default_language'),
    categories: pick(d, 'categories', 'categories'),
    tags: pick(d, 'tags', 'tags'),
    website_url: pick(d, 'websiteUrl', 'website_url'),
    privacy_policy_url: pick(d, 'privacyPolicyUrl', 'privacy_policy_url'),
    terms_url: pick(d, 'termsUrl', 'terms_url'),
    support_account: support
      ? {
          username: pick<string>(support, 'username', 'username'),
          nickname: pick<string>(support, 'nickname', 'nickname'),
          avatar_url: pick<string>(support, 'avatarUrl', 'avatar_url'),
          space_name: pick<string>(support, 'spaceName', 'space_name'),
        }
      : undefined,
    localizations: pick(d, 'localizations', 'localizations'),
    changelog: pick(d, 'changelog', 'changelog'),
    main_package: normalizeMainPackage(
      asRecord(pick(d, 'mainPackage', 'main_package')),
    ),
    platform_packages: normalizePlatformPackages(
      pick(d, 'platformPackages', 'platform_packages'),
    ),
    scope_snapshot: pick(d, 'scopeSnapshot', 'scope_snapshot'),
    permission_diff: pick(d, 'permissionDiff', 'permission_diff'),
    event_diff: pick(d, 'eventDiff', 'event_diff'),
  }
}

function normalizeMainUpload(raw: RawRecord): NormalizedMainUpload {
  const app_id = pick<string>(raw, 'appId', 'app_id') ?? ''
  const version_id = pick<string>(raw, 'versionId', 'version_id') ?? ''
  const status =
    pick<VersionStatus>(raw, 'status', 'status') ?? 'developing'
  const draftRaw = pick(raw, 'draft', 'draft')

  return {
    upload_id: pick<string>(raw, 'uploadId', 'upload_id') ?? '',
    package_type: pick<string>(raw, 'packageType', 'package_type'),
    file_name: pick<string>(raw, 'fileName', 'file_name') ?? 'package.tgz',
    file_size: pick<number>(raw, 'fileSize', 'file_size') ?? 0,
    parse_status: pick<string>(raw, 'parseStatus', 'parse_status'),
    app_id,
    version_id,
    status,
    platforms: normalizePlatforms(pick(raw, 'platforms', 'platforms')),
    draft: normalizeUploadDraft(draftRaw, { app_id, version_id, status }),
  }
}

function bytesToSizeMB(bytes: number): number {
  return Math.max(1, Math.round(bytes / 1024 / 1024)) || 1
}

function buildMainParse(data: NormalizedMainUpload): MainPackageInfo {
  const draft = data.draft
  const mp = draft?.main_package
  const file_name = data.file_name || mp?.file_name || 'package.tgz'
  const file_size = data.file_size || mp?.file_size || 0
  const platforms = data.platforms ?? draft?.platforms ?? []

  return {
    file_name,
    size_mb: bytesToSizeMB(file_size),
    version_code: draft?.version_code ?? '0.0.0',
    icon: mp?.icon_url,
    platforms,
    needs_platform_packages: data.needs_platform_packages ?? platforms.length > 0,
    runtimes: mp?.runtimes,
  }
}

/** parse-result 返回的可沿用历史包提示 */
export interface HistoricalPackageHint {
  platform: Platform
  package_version?: string
  from_version_id?: string
  package_url?: string
}

export interface ParseResult {
  upload_id: string
  parse_status?: string
  version_code?: string
  appName?: string
  icon_url?: string
  app_id?: string
  version_id?: string
  status?: VersionStatus
  platforms?: Platform[]
  /** 是否需要分平台子包；以服务端为准，不要用 platforms.length 推断 */
  needs_platform_packages?: boolean
  /** 清单 runtimes：desktop / web */
  runtimes?: MainPackageInfo['runtimes']
  historical_packages?: HistoricalPackageHint[]
  /** parse_status=failed 时的摘要，可直接展示 */
  error_message?: string
  details?: string[]
}

export function normalizeHistoricalPackages(
  raw: unknown,
): HistoricalPackageHint[] {
  if (!Array.isArray(raw)) return []
  const out: HistoricalPackageHint[] = []
  for (const item of raw) {
    const r = asRecord(item)
    if (!r) continue
    const platform = normalizePlatform(pick(r, 'platform', 'platform'))
    if (!platform || out.some((x) => x.platform === platform)) continue
    out.push({
      platform,
      package_version: pick<string>(r, 'packageVersion', 'package_version'),
      from_version_id: pick<string>(r, 'fromVersionId', 'from_version_id'),
      package_url: pick<string>(r, 'packageUrl', 'package_url'),
    })
  }
  return out
}

/**
 * 把 parse-result 的 historical_packages 写入草稿 platform_packages（is_historical）。
 * 已有本版本新上传的包（有 package_url / upload_id）不被覆盖。
 */
export function applyHistoricalToDraft(
  draft: VersionDetailDto,
  historical: HistoricalPackageHint[],
  declaredPlatforms: Platform[],
): VersionDetailDto {
  // 通用包（未声明平台）不需要平台包，也就无所谓沿用
  if (!historical.length || !declaredPlatforms.length) return draft
  const declared = new Set(declaredPlatforms)
  const byPlatform = new Map<
    Platform,
    NonNullable<VersionDetailDto['platform_packages']>[number]
  >()
  for (const p of draft.platform_packages ?? []) {
    const platform = normalizePlatform(p.platform)
    if (platform) byPlatform.set(platform, { ...p, platform })
  }
  for (const h of historical) {
    if (!declared.has(h.platform) || !h.from_version_id?.trim()) continue
    const cur = byPlatform.get(h.platform)
    if (
      cur &&
      !cur.is_historical &&
      cur.parse_status !== 'pending' &&
      cur.parse_status !== 'failed' &&
      (cur.package_url || cur.upload_id || cur.file_id)
    ) {
      continue
    }
    byPlatform.set(h.platform, {
      platform: h.platform,
      package_version: h.package_version,
      from_version_id: h.from_version_id,
      package_url: h.package_url,
      is_historical: true,
    })
  }
  return { ...draft, platform_packages: [...byPlatform.values()] }
}

/** GET /developer/packages/:upload_id/parse-result — 补齐 platforms / 历史包等（调一次，不轮询） */
export async function getParseResult(
  upload_id: string,
  signal?: AbortSignal,
): Promise<ParseResult> {
  const raw = await request<RawRecord>(
    `/developer/packages/${upload_id}/parse-result`,
    { signal },
  )
  return {
    upload_id: pick<string>(raw, 'upload_id', 'upload_id') ?? upload_id,
    parse_status: pick<string>(raw, 'parse_status', 'parse_status'),
    version_code: pick<string>(raw, 'version_code', 'version_code'),
    appName: pick<string>(raw, 'appName', 'app_name'),
    icon_url: pick<string>(raw, 'icon_url', 'icon_url'),
    app_id: pick<string>(raw, 'app_id', 'app_id'),
    version_id: pick<string>(raw, 'version_id', 'version_id'),
    status: pick<VersionStatus>(raw, 'status', 'status'),
    platforms: normalizePlatforms(pick(raw, 'platforms', 'platforms')),
    needs_platform_packages: pick<boolean>(
      raw,
      'needsPlatformPackages',
      'needs_platform_packages',
    ),
    runtimes: normalizeRuntimes(pick(raw, 'runtimes', 'runtimes')),
    historical_packages: normalizeHistoricalPackages(
      pick(raw, 'historicalPackages', 'historical_packages'),
    ),
    error_message: pick<string>(raw, 'errorMessage', 'error_message'),
    details: Array.isArray(raw.details)
      ? raw.details.filter((item): item is string => typeof item === 'string')
      : undefined,
  }
}

/** 解析失败时优先用 error_message，其次用 details */
export function parseFailureMessage(parse: Pick<ParseResult, 'error_message' | 'details'>): string {
  const summary = parse.error_message?.trim()
  if (summary) return summary
  const detail = parse.details?.find((item) => item.trim())
  return detail?.trim() ?? ''
}

/** 详情没带 runtimes 时，用 parse-result 补上。详情已有则不覆盖。 */
export function withRuntimes(
  draft: VersionDetailDto,
  parse: Pick<ParseResult, 'runtimes'>,
): VersionDetailDto {
  if (!parse.runtimes?.length || draft.main_package?.runtimes?.length) return draft
  return {
    ...draft,
    main_package: {
      ...draft.main_package,
      runtimes: parse.runtimes,
    },
  }
}

/** 把 parse-result 的 needs_platform_packages 写进主包（服务端没给则保持原样） */
export function withNeedsPlatformPackages(
  draft: VersionDetailDto,
  parse: Pick<ParseResult, 'needs_platform_packages'>,
): VersionDetailDto {
  if (parse.needs_platform_packages === undefined) return draft
  return {
    ...draft,
    main_package: {
      ...draft.main_package,
      needs_platform_packages: parse.needs_platform_packages,
    },
  }
}

/** 用 parse-result 补齐 upload 响应里常缺的 platforms / icon / version / 历史包 */
function enrichWithParseResult(
  data: NormalizedMainUpload,
  parse: ParseResult,
): NormalizedMainUpload {
  if (parse.parse_status === 'failed') return data

  let draft = data.draft ? { ...data.draft } : {}
  if (parse.version_code && !draft.version_code) {
    draft.version_code = parse.version_code
  }
  if (parse.platforms?.length && !draft.platforms?.length) {
    draft.platforms = parse.platforms
  }
  if (parse.icon_url) {
    draft.main_package = {
      ...draft.main_package,
      icon_url: draft.main_package?.icon_url ?? parse.icon_url,
    }
  }
  draft = withNeedsPlatformPackages(draft, parse)
  draft = withRuntimes(draft, parse)

  const platforms = data.platforms?.length
    ? data.platforms
    : (parse.platforms ?? draft.platforms ?? [])
  draft = applyHistoricalToDraft(
    draft,
    parse.historical_packages ?? [],
    platforms,
  )

  return {
    ...data,
    app_id: data.app_id || parse.app_id || '',
    version_id: data.version_id || parse.version_id || '',
    status: data.status || parse.status || 'developing',
    platforms,
    needs_platform_packages:
      parse.needs_platform_packages ?? data.needs_platform_packages,
    draft,
  }
}

/**
 * 上传主包：同步解析并落 developing 草稿。
 * 首次不传 app_id/version_id；同草稿覆盖时两者都传（snake_case 表单字段）。
 * 重传主包只有带上当前草稿的 version_id 才会修改该草稿，否则服务端不覆盖。
 * 成功后再调一次 parse-result，补齐 platforms / needs_platform_packages / 历史包。
 */
export async function uploadMainPackage(
  file: File,
  options: UploadMainOptions = {},
): Promise<UploadMainResult> {
  const form = new FormData()
  form.append('file', file)
  if (options.app_id) form.append('app_id', options.app_id)
  if (options.version_id) form.append('version_id', options.version_id)

  const raw = await request<RawRecord>('/developer/packages/upload', {
    method: 'POST',
    body: form,
    signal: options.signal,
    onUploadProgress: options.onUploadProgress,
  })
  let data = normalizeMainUpload(raw)
  let appName: string | undefined

  if (data.upload_id) {
    options.onParsingStart?.()
    try {
      const parse = await getParseResult(data.upload_id, options.signal)
      if (parse.parse_status === 'failed') {
        throw new PackageParseError(parseFailureMessage(parse))
      }
      data = enrichWithParseResult(data, parse)
      appName = parse.appName
    } catch (err) {
      if (err instanceof PackageParseError) throw err
      // 补齐失败不阻断上传成功；UI 仍可用 draft 基础字段
    }
  }

  return {
    upload_id: data.upload_id,
    app_id: data.app_id,
    version_id: data.version_id,
    status: data.status,
    mainParse: buildMainParse(data),
    draft: mapVersionDetail(data.draft ?? {}),
    appName,
  }
}

interface PlatformUploadTicket {
  upload_id: string
  put_url: string
  put_method: string
  put_headers: Record<string, string>
}

function platformPackagesPath(app_id: string, version_id: string): string {
  return `/developer/apps/${app_id}/versions/${version_id}/platform-packages`
}

function assertNotAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('Aborted', 'AbortError')
  }
}

/** 选文件后本地算 SHA-256，小写 64 位 hex；init 必带 */
export async function sha256Hex(
  file: Blob,
  signal?: AbortSignal,
): Promise<string> {
  assertNotAborted(signal)
  const buffer = await file.arrayBuffer()
  assertNotAborted(signal)
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  assertNotAborted(signal)
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** POST .../platform-packages/init — 申请 R2 预签名 PUT */
async function initPlatformUpload(input: {
  app_id: string
  version_id: string
  platform: Platform
  file: File
  sha256: string
  signal?: AbortSignal
}): Promise<PlatformUploadTicket> {
  const raw = await request<RawRecord>(
    `${platformPackagesPath(input.app_id, input.version_id)}/init`,
    {
      method: 'POST',
      body: {
        platform: input.platform,
        file_name: input.file.name,
        // 服务端 complete 时按此核对 R2 对象大小
        file_size: input.file.size,
        sha256: input.sha256,
      },
      signal: input.signal,
    },
  )
  const upload_id = pick<string>(raw, 'uploadId', 'upload_id') ?? ''
  const put_url = pick<string>(raw, 'putUrl', 'put_url') ?? ''
  if (!upload_id || !put_url) {
    throw new Error('Invalid platform package upload ticket')
  }
  const headers = asRecord(pick(raw, 'putHeaders', 'put_headers')) ?? {}
  return {
    upload_id,
    put_url,
    put_method: pick<string>(raw, 'putMethod', 'put_method') || 'PUT',
    put_headers: Object.fromEntries(
      Object.entries(headers).map(([k, v]) => [k, String(v)]),
    ),
  }
}

/** 浏览器直传 R2：只带 put_headers，不能带开发者 Authorization */
async function putToStorage(
  ticket: PlatformUploadTicket,
  file: File,
  signal: AbortSignal | undefined,
  onUploadProgress: ((progress: UploadProgress) => void) | undefined,
): Promise<void> {
  const res = await sendWithXhr(
    ticket.put_url,
    ticket.put_method,
    new Headers(ticket.put_headers),
    file,
    signal,
    onUploadProgress ?? (() => {}),
  )
  if (!res.ok) {
    throw new Error(`Storage upload failed: ${res.status}`)
  }
}

/** POST .../platform-packages/:uploadId/complete — 核对对象并挂到草稿 */
function completePlatformUpload(input: {
  app_id: string
  version_id: string
  upload_id: string
  signal?: AbortSignal
}): Promise<RawRecord> {
  return request<RawRecord>(
    `${platformPackagesPath(input.app_id, input.version_id)}/${input.upload_id}/complete`,
    { method: 'POST', body: {}, signal: input.signal },
  )
}

/**
 * 上传运行平台包：算 sha256 → init → 直传 R2 → complete，complete 成功即已挂到草稿。
 * 任一步失败整体重来（重新 init）；PUT 失败不调 complete。
 */
export async function uploadPlatformPackage(input: {
  app_id: string
  version_id: string
  platform: Platform
  file: File
  signal?: AbortSignal
  onUploadProgress?: (progress: UploadProgress) => void
}): Promise<UploadPlatformResult> {
  const sha256 = await sha256Hex(input.file, input.signal)
  const ticket = await initPlatformUpload({ ...input, sha256 })
  await putToStorage(ticket, input.file, input.signal, input.onUploadProgress)
  const raw = {
    upload_id: ticket.upload_id,
    ...(await completePlatformUpload({
      app_id: input.app_id,
      version_id: input.version_id,
      upload_id: ticket.upload_id,
      signal: input.signal,
    })),
  }

  return {
    upload_id: pick<string>(raw, 'uploadId', 'upload_id') ?? '',
    platform:
      normalizePlatform(pick(raw, 'platform', 'platform')) ?? input.platform,
    file_name: pick<string>(raw, 'fileName', 'file_name') ?? input.file.name,
    file_size: pick<number>(raw, 'fileSize', 'file_size') ?? input.file.size,
    package_url: pick<string>(raw, 'packageUrl', 'package_url'),
    app_id: pick<string>(raw, 'appId', 'app_id') ?? input.app_id,
    version_id: pick<string>(raw, 'versionId', 'version_id') ?? input.version_id,
  }
}
